/* eslint-disable react-hooks/rules-of-hooks -- Added before disable comments where required */
import { useMutation } from "react-query";
import { useHistory } from "react-router-dom";
import { t } from "@/i18n-js/instance";
import { useEditEventModal } from "@circle-react/components/Modals/EditEventModal";
import { SpaceSelect } from "@circle-react/components/shared/SpaceSelect";
import {
  useCurrentSpaceContext,
  useSpacesContext,
} from "@circle-react/contexts";
import { fetchRequest } from "@circle-react/helpers/backendRequestHelpers";
import { eventsShowPath, internalApi } from "@circle-react/helpers/urlHelpers";
import type { Event } from "@circle-react/types/Event";
import { Button } from "@circle-react-uikit/Button";
import { Form } from "@circle-react-uikit/Form";
import { Modal } from "@circle-react-uikit/Modal";
import { useToast } from "@circle-react-uikit/ToastV2";

const DISPLAY_EDIT_MODAL_DELAY_MILLISECONDS = 500;

interface DuplicatedEventParams {
  space_id?: number;
}

interface DuplicateEventModalProps {
  event: Event;
  onClose: () => void;
  show: boolean;
}

export const DuplicateEventModal = ({
  event,
  onClose,
  show,
}: DuplicateEventModalProps) => {
  const history = useHistory();
  const { success, error } = useToast();
  const {
    isLoading,
    helpers: { findById: findSpace, spacesWithEventCreationAllowed },
  } = useSpacesContext();
  if (isLoading) return null;
  const spaces = spacesWithEventCreationAllowed();
  const { data: space } = useCurrentSpaceContext();
  const editEventModal = useEditEventModal();
  const handleDuplicate = useMutation<Event, Error, DuplicatedEventParams>(
    async data => {
      const response = await fetchRequest<Event>(
        internalApi.events.duplicate({
          spaceId: event.space_id,
          slug: event.slug,
        }),
        {
          method: "PUT",
          body: JSON.stringify({
            event: {
              event_id: event.id,
              space_id: data.space_id,
            },
          }),
          forReactQuery: true,
        },
      );
      if (!response || !("id" in response)) {
        throw new Error("Failed to duplicate event");
      }
      return response as Event;
    },
    {
      onSuccess: duplicatedEvent => {
        const eventSpace = findSpace(duplicatedEvent.space_id);
        if (!eventSpace) {
          error(t("space_not_found"));
          return;
        }
        history.push(
          eventsShowPath({
            spaceSlug: eventSpace.slug,
            eventSlug: duplicatedEvent.slug,
          }),
        );
        setTimeout(
          () => void editEventModal.show({ event: duplicatedEvent }),
          DISPLAY_EDIT_MODAL_DELAY_MILLISECONDS,
        );
        success(t("event_duplicated_successfully"));
      },
      onError: () => {
        error(t("event_duplication_failed"));
      },
    },
  );

  return (
    <Modal
      overflowVisible
      title={t("duplicate_event_title", { event_title: event.name })}
      show={show}
      onClose={onClose}
    >
      <Form
        onSubmit={values => {
          handleDuplicate.mutate({ space_id: values.space_id });
        }}
        defaultValues={{
          space_id: space?.id,
        }}
      >
        <Form.Item
          name="space_id"
          hideBorder
          fullWidth
          description={t("duplicate_event_to_space")}
          rules={{
            required: t("events.create.form.space_required_error"),
          }}
        >
          <SpaceSelect
            spaces={spaces}
            name="space_id"
            variant="form-input"
            defaultValue={space?.id}
          />
        </Form.Item>
        <p className="modal__small text-dark mb-4">
          {t("attendees_wont_be_duplicated")}
        </p>
        <Button type="submit" variant="primary">
          {t("duplicate")}
        </Button>
      </Form>
    </Modal>
  );
};
